import * as THREE from 'three';
import { fbm2, noise2, smoothstep, lerp, hash2 } from './util.js';
import { carveAt, bedDepthAt, VILLAGE_H, WORLD_R, lakes, BRIDGE } from './layout.js';

export const MILL_HILL = { x: -57, z: -49, sigma: 8.5, h: 5.2 };

/** Terrain height WITHOUT river / lake carving (roads and bridges follow this). */
export function baseHeightAt(x, z) {
  const r = Math.hypot(x, z);
  let h = 0.95 + 0.42 * fbm2(x * 0.022, z * 0.022, 3) + 0.1 * noise2(x * 0.09 + 7, z * 0.09 - 3);
  // flat, tidy village floor
  h = lerp(h, VILLAGE_H, 1 - smoothstep(24, 40, r));
  // knoll for the windmill
  const dm2 = (x - MILL_HILL.x) ** 2 + (z - MILL_HILL.z) ** 2;
  h += MILL_HILL.h * Math.exp(-dm2 / (2 * MILL_HILL.sigma ** 2));
  // encircling hills + far mountains
  const rm = smoothstep(74, 128, r);
  h += rm * rm * (13 + 26 * (0.5 + 0.5 * fbm2(x * 0.02 + 11, z * 0.02 - 5, 4)));
  h += smoothstep(118, 190, r) * (16 + 20 * (0.5 + 0.5 * fbm2(x * 0.014, z * 0.014 + 9, 3)));
  return h;
}

/** Final terrain height (with water carving). */
export function heightAt(x, z) {
  const base = baseHeightAt(x, z);
  const c = carveAt(x, z);
  return c > 0 ? lerp(base, bedDepthAt(x, z), c) : base;
}

export function slopeAt(x, z, e = 0.8) {
  const dx = heightAt(x + e, z) - heightAt(x - e, z);
  const dz = heightAt(x, z + e) - heightAt(x, z - e);
  return Math.hypot(dx, dz) / (2 * e);
}

const C = {
  g1: new THREE.Color('#79cc49'),
  g2: new THREE.Color('#5cb443'),
  g3: new THREE.Color('#9ad95b'),
  dry: new THREE.Color('#bcd85c'),
  hill: new THREE.Color('#4a9a45'),
  hillDark: new THREE.Color('#2f7c4b'),
  rock: new THREE.Color('#a29d96'),
  rockDark: new THREE.Color('#7b7680'),
  sand: new THREE.Color('#efdca4'),
  bed: new THREE.Color('#7fa39a'),
  snow: new THREE.Color('#f6f9ff'),
};

const _tmp = new THREE.Color();
function terrainColor(x, z, h, ny, out) {
  const r = Math.hypot(x, z);
  const n1 = fbm2(x * 0.05 + 3, z * 0.05 + 8, 2);
  const n2 = noise2(x * 0.013 - 4, z * 0.013 + 1);
  out.copy(C.g2).lerp(C.g1, smoothstep(-0.25, 0.25, n1));
  out.lerp(C.g3, smoothstep(0.3, 0.6, n1) * 0.65);
  out.lerp(C.dry, smoothstep(0.2, 0.7, n2) * 0.5);
  out.lerp(C.g3, (1 - smoothstep(10, 36, r)) * 0.35); // tidy village lawn
  out.lerp(C.hill, smoothstep(3, 20, h) * 0.85);
  out.lerp(C.hillDark, smoothstep(14, 32, h) * 0.65);
  const slope = 1 - ny;
  _tmp.copy(C.rock).lerp(C.rockDark, smoothstep(0, 0.6, n1 * 0.5 + 0.5));
  out.lerp(_tmp, smoothstep(0.3, 0.5, slope + n1 * 0.04) * smoothstep(3, 14, h));
  const snow = smoothstep(31, 40, h + n2 * 5) * smoothstep(0.5, 0.8, ny + 0.15);
  out.lerp(C.snow, snow);
  // shores + river bed
  out.lerp(C.sand, 1 - smoothstep(0.05, 0.6, h));
  out.lerp(C.bed, smoothstep(-0.1, -1.2, h) * 0.85);
  return out;
}

export function createTerrain() {
  const N = 300;
  const R = WORLD_R;
  const cell = (2 * R) / N;
  let geo = new THREE.PlaneGeometry(2 * R, 2 * R, N, N);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const ix = i % (N + 1), iz = Math.floor(i / (N + 1));
    let x = pos.getX(i), z = pos.getZ(i);
    if (ix > 0 && iz > 0 && ix < N && iz < N) {
      x += (hash2(ix, iz, 1) - 0.5) * cell * 0.8;
      z += (hash2(ix, iz, 2) - 0.5) * cell * 0.8;
    }
    pos.setXYZ(i, x, heightAt(x, z), z);
  }
  geo = geo.toNonIndexed();
  geo.deleteAttribute('uv');
  geo.computeVertexNormals();

  const p = geo.attributes.position, nrm = geo.attributes.normal;
  const colors = new Float32Array(p.count * 3);
  const c = new THREE.Color();
  for (let i = 0; i < p.count; i += 3) {
    const cx = (p.getX(i) + p.getX(i + 1) + p.getX(i + 2)) / 3;
    const cy = (p.getY(i) + p.getY(i + 1) + p.getY(i + 2)) / 3;
    const cz = (p.getZ(i) + p.getZ(i + 1) + p.getZ(i + 2)) / 3;
    terrainColor(cx, cz, cy, nrm.getY(i), c);
    const k = 1 + (hash2(Math.floor(cx * 3), Math.floor(cz * 3), 9) - 0.5) * 0.09;
    for (let j = 0; j < 3; j++) {
      colors[(i + j) * 3] = c.r * k;
      colors[(i + j) * 3 + 1] = c.g * k;
      colors[(i + j) * 3 + 2] = c.b * k;
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 }),
  );
  mesh.receiveShadow = true;
  mesh.name = 'terrain';
  return mesh;
}

/** Ground-height texture used by the water shader for depth + shoreline foam. */
export function createHeightTexture() {
  const cx = (lakes[0].x + lakes[1].x) / 2, cz = (lakes[0].z + lakes[1].z) / 2;
  const size = 96; // metres covered
  const res = 384;
  const data = new Uint8Array(res * res);
  for (let j = 0; j < res; j++) {
    for (let i = 0; i < res; i++) {
      const x = cx + ((i + 0.5) / res - 0.5) * size;
      const z = cz + ((j + 0.5) / res - 0.5) * size;
      const h = Math.max(-1.5, Math.min(1.5, heightAt(x, z)));
      data[j * res + i] = Math.round(((h + 1.5) / 3) * 255);
    }
  }
  const tex = new THREE.DataTexture(data, res, res, THREE.RedFormat, THREE.UnsignedByteType);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return { texture: tex, center: new THREE.Vector2(cx, cz), size };
}
